import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Container, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { theme } from './theme/theme';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthenticatedNav } from './components/AuthenticatedNav';
import PWAInstallBanner from './components/PWAInstallBanner';
import PWAInstallService from './services/pwaInstallService';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { ForgotPassword } from './pages/ForgotPassword';
import { Contact } from './pages/Contact';
import { About } from './pages/About';
import { Dashboard } from './pages/Dashboard';
import { EducationPlaceholder } from './pages/EducationPlaceholder';
import { EconomicCalendarPlaceholder } from './pages/EconomicCalendarPlaceholder';
import { ExpertAdvisors } from './pages/ExpertAdvisors';
import { SignalsPlaceholder } from './pages/SignalsPlaceholder';
import { Community } from './pages/Community';
import { MarketIntelligence } from './pages/MarketIntelligence';
import { AIAnalyses } from './pages/AIAnalyses';
import { Profile } from './pages/Profile';
import { Settings } from './pages/Settings';
import { Chart } from './pages/Chart';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminUserManagement } from './pages/AdminUserManagement';
import { AdminContactMessages } from './pages/AdminContactMessages';
import { AdminLoyaltyConfig } from './pages/AdminLoyaltyConfig';
import { AdminReports } from './pages/AdminReports';
import { AdminNotifications } from './pages/AdminNotifications';
import { AdminProductManagement } from './pages/AdminProductManagement';
import { AdminDownloadsPage } from './pages/AdminDownloadsPage';
import { AdminTradingJournalReport } from './pages/AdminTradingJournalReport';
import { AdminFeedback } from './pages/AdminFeedback';
import { Feedback } from './pages/Feedback';
import TradingJournal from './pages/TradingJournal';
import { TradingJournalProvider } from './contexts/TradingJournalContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import { Competition } from './pages/Competition';

// Component to conditionally render Header
const ConditionalHeader: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const authPages = ['/login', '/signup'];
  
  // Don't show any navigation on auth pages
  if (authPages.includes(location.pathname)) {
    return null;
  }
  
  // Show AuthenticatedNav for logged in users, Header for non-logged in users
  return currentUser ? <AuthenticatedNav /> : <Header />;
};

// Component to handle home page logic - redirect to dashboard or community if logged in
const HomeOrDashboard: React.FC = () => {
  const { currentUser, loading } = useAuth();
  const [defaultToCommunity, setDefaultToCommunity] = React.useState<boolean | null>(null);
  const [userDataLoading, setUserDataLoading] = React.useState(true);
  const [retryCount, setRetryCount] = React.useState(0);
  const [isPWAStandalone, setIsPWAStandalone] = React.useState(false);

  // Detect if running in PWA standalone mode
  React.useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true ||
                        document.referrer.includes('android-app://');
    setIsPWAStandalone(isStandalone);
    
    if (isStandalone) {
      console.log('🔍 PWA standalone mode detected - will ensure user preferences are loaded');
    }
  }, []);
  
  // Load user's default page preference with retry logic for PWA
  React.useEffect(() => {
    const loadUserPreference = async () => {
      if (!currentUser) {
        setUserDataLoading(false);
        return;
      }
      
      try {
        // Add extra delay for PWA to ensure Firebase is fully initialized
        if (isPWAStandalone && retryCount === 0) {
          console.log('⏳ PWA detected - waiting for Firebase initialization...');
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
        
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('./config/firebase');
        
        console.log('📱 Loading user preference for defaultToCommunity...');
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const defaultToCommunityValue = userData.defaultToCommunity || false;
          console.log('✅ User preference loaded:', { defaultToCommunity: defaultToCommunityValue });
          setDefaultToCommunity(defaultToCommunityValue);
        } else {
          console.log('⚠️ User document does not exist, using default');
          setDefaultToCommunity(false);
        }
      } catch (error) {
        console.error('❌ Error loading user preference:', error);
        
        // Retry logic for PWA - sometimes Firebase needs more time
        if (isPWAStandalone && retryCount < 2) {
          console.log(`🔄 Retrying user preference load (attempt ${retryCount + 1})...`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            setUserDataLoading(true);
          }, 1000);
          return;
        }
        
        setDefaultToCommunity(false);
      } finally {
        setUserDataLoading(false);
      }
    };
    
    loadUserPreference();
  }, [currentUser, retryCount, isPWAStandalone]);
  
  // Show loading while checking authentication or user data
  if (loading || (userDataLoading && currentUser)) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ backgroundColor: 'background.default' }}
      >
        <Box sx={{ mb: 2 }}>
          {/* Simple CSS spinner */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '4px solid rgba(255, 215, 0, 0.1)',
              borderTop: '4px solid #FFD700',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 1 }}>
          T1 FX Team
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isPWAStandalone ? 'Initializing app...' : 'Loading...'}
        </Typography>
        {isPWAStandalone && retryCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Syncing preferences...
          </Typography>
        )}
      </Box>
    );
  }
  
  // If user is logged in, redirect to their preferred default page
  if (currentUser && defaultToCommunity !== null) {
    const defaultPage = defaultToCommunity ? '/community' : '/dashboard';
    console.log(`🚀 Redirecting authenticated user to: ${defaultPage}`);
    return <Navigate to={defaultPage} replace />;
  }
  
  // If user is not logged in, show home page
  return <Home />;
};

// Wrapper component for authenticated pages
const AuthenticatedPageWrapper: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => {
  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ mb: 2 }}>
            {title}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            {subtitle}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This feature is currently under development and will be available soon.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

// Main app content component that has access to auth context
const AppContent: React.FC = () => {
  const { currentUser } = useAuth();

  // Initialize PWA install service on app load
  useEffect(() => {
    const pwaService = PWAInstallService.getInstance();
    pwaService.initializeSession();
  }, []);

  return (
    <Router>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <ConditionalHeader />
        <Box component="main" sx={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<HomeOrDashboard />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trading-journal"
              element={
                <ProtectedRoute>
                  <TradingJournalProvider userId={currentUser?.uid}>
                    <TradingJournal />
                  </TradingJournalProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chart"
              element={
                <ProtectedRoute>
                  <Chart />
                </ProtectedRoute>
              }
            />
            <Route
              path="/market-intelligence"
              element={
                <ProtectedRoute>
                  <MarketIntelligence />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-analyses"
              element={
                <ProtectedRoute>
                  <AIAnalyses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition"
              element={
                <ProtectedRoute>
                  <Competition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/education"
              element={
                <ProtectedRoute>
                  <EducationPlaceholder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/economic-calendar"
              element={
                <ProtectedRoute>
                  <EconomicCalendarPlaceholder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expert-advisors"
              element={
                <ProtectedRoute>
                  <ExpertAdvisors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community"
              element={
                <ProtectedRoute>
                  <Community />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signals"
              element={
                <ProtectedRoute>
                  <SignalsPlaceholder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminUserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contact-messages"
              element={
                <ProtectedRoute>
                  <AdminContactMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/loyalty-config"
              element={
                <ProtectedRoute>
                  <AdminLoyaltyConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute>
                  <AdminReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <ProtectedRoute>
                  <AdminNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute>
                  <AdminProductManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/downloads"
              element={
                <ProtectedRoute>
                  <AdminDownloadsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/trading-journal-report"
              element={
                <ProtectedRoute>
                  <AdminTradingJournalReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <ProtectedRoute>
                  <AdminFeedback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <Feedback />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Box>
        <Footer />
        <PWAInstallBanner />
      </Box>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <UserSettingsProvider>
          <AppContent />
        </UserSettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
